/**
 * Reveals a freshly created outline child in the server-side tree.
 *
 * The hard case is a parent that was a LEAF when the grid last loaded it. `RowNode.updateData` only
 * swaps the data object; it never re-runs the SSRM tree-group pass, so a leaf stays a leaf:
 * `hasChildren()` is still false, `key` is still null, `getRoute()` therefore returns undefined, and
 * `setExpanded(true)` builds a child store whose request carries a null group key. The child loads
 * (or does not) and is never displayed.
 *
 * PATH CHOSEN: `applyServerSideTransaction({ route, update: [...] })`. Verified in AG Grid 36.1.0
 * enterprise source: `TransactionManager.applyTransaction` -> `executeOnStore(transaction.route)` ->
 * `LazyStore.applyTransaction` -> `LazyCache.updateRowNodes` -> `BlockUtils.updateDataIntoRowNode`,
 * which under `treeData` calls `setTreeGroupInfo` — that re-runs `updateHasChildren()` (reading the
 * grid's `isServerSideGroup` against the NEW data) and re-reads `getServerSideGroupKey`. So flipping
 * `hasChildren: true` through a transaction genuinely promotes the leaf to a group with a real key,
 * and the subsequent `setExpanded(true)` opens a correctly-routed child store.
 *
 * The transaction's `route` is the route of the store CONTAINING the parent — i.e. the parent's own
 * parent's route — because `executeOnStore` resolves `getChildStore(route)` and the update is keyed
 * by row id within that store. A parent sitting in the root store therefore uses `[]`; the root node
 * has `level === -1` so its `getRoute()` already answers `[]`.
 *
 * Nothing here imports a grid type. The API surface is the handful of members this orchestration
 * touches, so the whole thing is exercisable from a plain object in a unit test.
 */

/** Bail-out for a child that never arrives — a failed request must not leave the caller hanging. */
const OUTLINE_REVEAL_TIMEOUT_MS = 1500;

export interface OutlineRevealNode {
  data?: { hasChildren?: boolean } | null;
  expanded?: boolean;
  /**
   * Present once the node has been opened at least once. Not on the public `IRowNode` — it is read
   * here purely as a truthiness probe for "this node has a store that predates the new child".
   */
  childStore?: unknown;
  parent?: OutlineRevealNode | null;
  getRoute(): string[] | undefined;
  setExpanded(expanded: boolean): void;
}

export interface OutlineRevealApi {
  getRowNode(id: string): OutlineRevealNode | null | undefined;
  refreshServerSide(params?: { route?: string[]; purge?: boolean }): void;
  applyServerSideTransaction(transaction: { route?: string[]; update?: unknown[] }): unknown;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface OutlineRevealRequest {
  api: OutlineRevealApi;
  parentRowId: string;
  childRowId: string;
  setFocusedItemId?: (id: string) => void;
  timeoutMs?: number;
}

/**
 * Opens the parent onto the new child and focuses it once the grid has actually rendered the row.
 * On timeout nothing is focused: focusing a row that never loaded would scroll the board to a ghost.
 */
export async function revealOutlineChild(request: OutlineRevealRequest): Promise<void> {

  const { api, parentRowId, childRowId } = request;
  const parent = api.getRowNode(parentRowId);
  if (!parent) {
    // The parent itself is not loaded (collapsed ancestor, evicted block); only a full purge can help.
    api.refreshServerSide({ purge: true });
  } else {
    const containingRoute = parent.parent?.getRoute() ?? [];
    if (parent.data?.hasChildren !== true) {
      api.applyServerSideTransaction({ route: containingRoute, update: [{ ...parent.data, hasChildren: true }] });
      parent.setExpanded(true);
    } else if (parent.expanded === true) {
      const route = parent.getRoute();
      if (route) api.refreshServerSide({ route, purge: true });
      else api.refreshServerSide({ purge: true });
    } else {
      parent.setExpanded(true);
      // A cached store predates the new child, and re-expanding alone replays it verbatim.
      if (parent.childStore) {
        const route = parent.getRoute();
        if (route) api.refreshServerSide({ route, purge: true });
      }
    }
  }

  const loaded = await waitForOutlineRow(api, childRowId, request.timeoutMs ?? OUTLINE_REVEAL_TIMEOUT_MS);
  if (loaded) request.setFocusedItemId?.(childRowId);
}

/** Resolves true when the row is in the model, false when the timeout wins. */
function waitForOutlineRow(api: OutlineRevealApi, rowId: string, timeoutMs: number): Promise<boolean> {

  if (api.getRowNode(rowId)) return Promise.resolve(true);
  return new Promise<boolean>(resolve => {
    let settled = false;
    const finish = (found: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      api.removeEventListener('modelUpdated', onUpdate);
      resolve(found);
    };
    const onUpdate = () => {
      if (api.getRowNode(rowId)) finish(true);
    };
    // Timer first: a listener that fires synchronously on registration must find the handle initialised.
    const timer = setTimeout(() => finish(!!api.getRowNode(rowId)), timeoutMs);
    api.addEventListener('modelUpdated', onUpdate);
  });
}
